import React, { ReactNode, useCallback, useMemo } from 'react';
import { GiftCardProduct } from '@adapters/bigcommerce/types/product';

export interface ReducerState {
  giftCardRecipientsName?: string;
  giftCardSendersName?: string;
  giftCardRecipientsEmail: string;
  giftCardDeliveryDate: string;
  giftCardMessage?: string;
  isValidEmail?: boolean;
}

export interface DerivedState {
  giftCardRecipientsNameOptionId?: number;
  giftCardSendersNameOptionId?: number;
  giftCardRecipientsEmailOptionId?: number;
  giftCardDeliveryDateOptionId?: number;
  giftCardMessageOptionId?: number;
  minDeliveryDate?: string;
  maxDeliveryDate?: string;
}

export interface Actions {
  setGiftCardRecipientsName: (giftCardRecipientsName: string) => void;
  setGiftCardSendersName: (giftCardSendersName: string) => void;
  setGiftCardRecipientsEmail: (giftCardRecipientsEmail: string) => void;
  setGiftCardDeliveryDate: (giftCardDeliveryDate: string) => void;
  setGiftCardMessage: (giftCardMessage: string) => void;
  setIsValidEmail: (isValidEmail: boolean) => void;
}

export type GiftCardState = ReducerState & DerivedState & Actions;

type Action =
  | {
      type: 'SET_GIFTCARD_RECIPIENTS_NAME';
      giftCardRecipientsName: string;
    }
  | {
      type: 'SET_GIFTCARD_SENDERS_NAME';
      giftCardSendersName: string;
    }
  | {
      type: 'SET_GIFTCARD_RECIPIENTS_EMAIL';
      giftCardRecipientsEmail: string;
    }
  | {
      type: 'SET_GIFTCARD_DELIVERY_DATE';
      giftCardDeliveryDate: string;
    }
  | {
      type: 'SET_GIFTCARD_MESSAGE';
      giftCardMessage: string;
    }
  | {
      type: 'SET_IS_VALID_EMAIL';
      isValidEmail: boolean;
    };

const createInitialState = (today: string, oneYearFromToday: string) => ({
  giftCardRecipientsName: '',
  giftCardSendersName: '',
  giftCardRecipientsEmail: '',
  giftCardDeliveryDate: today,
  giftCardMessage: '',
  isValidEmail: false,
  minDeliveryDate: today,
  maxDeliveryDate: oneYearFromToday,
  setGiftCardRecipientsName: () => null,
  setGiftCardSendersName: () => null,
  setGiftCardRecipientsEmail: () => null,
  setGiftCardDeliveryDate: () => null,
  setGiftCardMessage: () => null,
  setIsValidEmail: () => null,
});

const today = new Date().toISOString().split('T')[0];
const oneYearFromToday = new Date(
  new Date().setFullYear(new Date().getFullYear() + 1)
)
  .toISOString()
  .split('T')[0];

export const GiftCardContext = React.createContext<GiftCardState>(
  createInitialState(today, oneYearFromToday)
);
GiftCardContext.displayName = 'GiftCardContext';

const giftCardUiReducer = (state: ReducerState, action: Action) => {
  switch (action.type) {
    case 'SET_GIFTCARD_RECIPIENTS_NAME': {
      return {
        ...state,
        giftCardRecipientsName: action.giftCardRecipientsName,
      };
    }
    case 'SET_GIFTCARD_SENDERS_NAME': {
      return {
        ...state,
        giftCardSendersName: action.giftCardSendersName,
      };
    }
    case 'SET_GIFTCARD_RECIPIENTS_EMAIL': {
      return {
        ...state,
        giftCardRecipientsEmail: action.giftCardRecipientsEmail,
      };
    }
    case 'SET_GIFTCARD_DELIVERY_DATE': {
      return {
        ...state,
        giftCardDeliveryDate: action.giftCardDeliveryDate,
      };
    }
    case 'SET_GIFTCARD_MESSAGE': {
      return {
        ...state,
        giftCardMessage: action.giftCardMessage,
      };
    }
    case 'SET_IS_VALID_EMAIL': {
      return {
        ...state,
        isValidEmail: action.isValidEmail,
      };
    }
    default: {
      return { ...state };
    }
  }
};

export interface PdpProviderProps {
  giftCard: GiftCardProduct | null;
  children: ReactNode;
}

export const GiftCardContextProvider = ({
  children,
  giftCard,
}: PdpProviderProps) => {
  const [state, dispatch] = React.useReducer(
    giftCardUiReducer,
    createInitialState(today, oneYearFromToday)
  );

  const setGiftCardRecipientsName = (giftCardRecipientsName: string) =>
    dispatch({ type: 'SET_GIFTCARD_RECIPIENTS_NAME', giftCardRecipientsName });

  const setGiftCardSendersName = (giftCardSendersName: string) =>
    dispatch({ type: 'SET_GIFTCARD_SENDERS_NAME', giftCardSendersName });

  const setGiftCardRecipientsEmail = (giftCardRecipientsEmail: string) =>
    dispatch({
      type: 'SET_GIFTCARD_RECIPIENTS_EMAIL',
      giftCardRecipientsEmail,
    });

  const setGiftCardDeliveryDate = (giftCardDeliveryDate: string) =>
    dispatch({
      type: 'SET_GIFTCARD_DELIVERY_DATE',
      giftCardDeliveryDate,
    });

  const setGiftCardMessage = (giftCardMessage: string) =>
    dispatch({
      type: 'SET_GIFTCARD_MESSAGE',
      giftCardMessage,
    });

  const setIsValidEmail = useCallback((isValidEmail: boolean) => {
    dispatch({
      type: 'SET_IS_VALID_EMAIL',
      isValidEmail,
    });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setGiftCardRecipientsName,
      setGiftCardSendersName,
      setGiftCardRecipientsEmail,
      setGiftCardDeliveryDate,
      setGiftCardMessage,
      setIsValidEmail,
      giftCardRecipientsNameOptionId: giftCard?.giftCardRecipientsNameOptionId,
      giftCardSendersNameOptionId: giftCard?.giftCardSendersNameOptionId,
      giftCardRecipientsEmailOptionId:
        giftCard?.giftCardRecipientsEmailOptionId,
      giftCardDeliveryDateOptionId: giftCard?.giftCardDeliveryDateOptionId,
      giftCardMessageOptionId: giftCard?.giftCardMessageOptionId,
    }),
    [state, giftCard, setIsValidEmail]
  );

  return (
    <GiftCardContext.Provider value={value}>
      {children}
    </GiftCardContext.Provider>
  );
};

export const useGiftCard = () => {
  const context = React.useContext(GiftCardContext);
  if (context === undefined) {
    throw new Error(`useGiftCard must be used within a GiftCardProvider`);
  }
  return context;
};
